import { TERRAIN, heightAt, sampleGround, surfaceAt, gripAt, rollingResistance, SURFACE } from '../terrain/heightfield.js';

// Chassis geometry
export const HALF_W = 0.92;          // Half track width (m)
export const WHEEL_BASE = 2.72;      // Wheelbase (m)
export const WHEEL_BASE_HALF = WHEEL_BASE / 2;
export const WHEEL_RADIUS = 0.45;    // Wheel radius (m)

// Drivetrain & Performance
export const ENGINE_POWER = 9500;    // N thrust
export const BRAKE_POWER = 35000;    // N braking force
export const HANDBRAKE_POWER = 48000;
export const MAX_STEER = 0.62;       // Max steering angle (rad, ~35 deg)
export const STEER_RATE = 3.8;       // Steering speed (rad/s)
export const STEER_RETURN = 5.6;     // Return-to-center speed (rad/s)
export const MAX_SPEED = 48;         // Top speed (~172 km/h)
export const REVERSE_MAX_SPEED = 14; // Reverse speed (~50 km/h)
export const BOOST_MULT = 1.58;      // Nitro boost power multiplier
export const BOOST_DRAIN = 0.30;     // Boost consumption rate (/s)
export const BOOST_REGEN = 0.16;     // Boost recharge rate (/s)

export const GEAR_SPEEDS = [0, 8.5, 17, 26, 35, 44, MAX_SPEED];
export const GRAVITY = 24.5;         // Exaggerated for responsive arcade gravity
export const MASS = 1650;            // kg

// 4-point Suspension
export const SUSP_K = 42000;         // Spring constant (N/m)
export const SUSP_C = 5200;          // Damper constant (N*s/m)
export const SUSP_DROOP = 0.62;      // Gap at full uncompressed droop (m)
export const SUSP_BUMP = 0.08;       // Gap at bump stop (m)
// Equilibrium rest compression: 4 * K * x = MASS * GRAVITY
export const SUSP_REST = (MASS * GRAVITY) / (4 * SUSP_K); // ~0.2406 m
export const REST_GAP = SUSP_DROOP - SUSP_REST;           // ~0.3794 m

function clamp(v, a, b) {
  return v < a ? a : v > b ? b : v;
}

function lerp(a, b, t) {
  return a + (b - a) * t;
}

export const WHEELS = [
  { id: 'FL', x: WHEEL_BASE_HALF, z: HALF_W, steered: true, driven: true },
  { id: 'FR', x: WHEEL_BASE_HALF, z: -HALF_W, steered: true, driven: true },
  { id: 'RL', x: -WHEEL_BASE_HALF, z: HALF_W, steered: false, driven: true },
  { id: 'RR', x: -WHEEL_BASE_HALF, z: -HALF_W, steered: false, driven: true }
];

export class Jeep {
  constructor() {
    this.pos = { x: 0, y: 0, z: 0 };
    this.vel = { x: 0, y: 0, z: 0 };
    this.yaw = 0;
    this.angularVel = 0;
    this.steer = 0;
    this.speed = 0;            // Signed forward speed (m/s)
    this.gear = 1;             // -1 = R, 0 = N, 1..6
    this.rpm = 900;
    this.boost = 1.0;
    this.boosting = false;
    this.slip = 0;             // Lateral slip factor 0..1
    this.surface = SURFACE.ROAD;
    this.suspension = [0.24, 0.24, 0.24, 0.24]; // Compression per wheel (m)
    this.wheelGaps = [REST_GAP, REST_GAP, REST_GAP, REST_GAP];
    this.wheelSlip = [0, 0, 0, 0];
    this.wheelSpin = 0;
    this.pitch = 0;
    this.roll = 0;
    this.contactCount = 4;
    this.onGround = true;
    this.airborne = false;
    this.airTime = 0;
  }

  reset(x, z, yaw) {
    this.pos.x = x;
    this.pos.z = z;
    this.pos.y = heightAt(x, z) + WHEEL_RADIUS + REST_GAP;
    this.vel.x = 0;
    this.vel.y = 0;
    this.vel.z = 0;
    this.yaw = yaw;
    this.angularVel = 0;
    this.steer = 0;
    this.speed = 0;
    this.gear = 1;
    this.rpm = 900;
    this.boost = 1.0;
    this.boosting = false;
    this.slip = 0;
    this.pitch = 0;
    this.roll = 0;
    this.suspension = [SUSP_REST, SUSP_REST, SUSP_REST, SUSP_REST];
    this.wheelGaps = [REST_GAP, REST_GAP, REST_GAP, REST_GAP];
    this.wheelSlip = [0, 0, 0, 0];
    this.wheelSpin = 0;
    this.contactCount = 4;
    this.onGround = true;
    this.airborne = false;
    this.airTime = 0;
    this.surface = surfaceAt(x, z, this.pos.y);
  }

  forwardVec() {
    return { x: Math.sin(this.yaw), z: Math.cos(this.yaw) };
  }

  rightVec() {
    return { x: Math.cos(this.yaw), z: -Math.sin(this.yaw) };
  }

  step(dt, input) {
    const MAX_SUBSTEP = 1 / 60;
    let remaining = dt;
    let guard = 0;
    while (remaining > 1e-5 && guard++ < 8) {
      const stepDt = Math.min(MAX_SUBSTEP, remaining);
      this.update(stepDt, input);
      remaining -= stepDt;
    }
  }

  update(dt, input) {
    // 1. Steering input with speed sensitivity
    const steerInput = (input.right ? 1 : 0) - (input.left ? 1 : 0);
    // Taper steering lock slightly at high speed for high-speed stability
    const speedFactor = Math.min(1, Math.abs(this.speed) / 28);
    const effectiveLock = MAX_STEER * (1.0 - speedFactor * 0.38);
    const steerTarget = steerInput * effectiveLock;
    const rate = steerInput !== 0 ? STEER_RATE : STEER_RETURN;
    this.steer += clamp(steerTarget - this.steer, -rate * dt, rate * dt);

    // 2. Boost handling
    const wantsBoost = !!input.boost && this.boost > 0.03 && (input.throttle || this.speed > 2);
    this.boosting = wantsBoost;
    if (this.boosting) {
      this.boost = Math.max(0, this.boost - BOOST_DRAIN * dt);
    } else {
      this.boost = Math.min(1.0, this.boost + BOOST_REGEN * dt);
    }

    const fwd = this.forwardVec();
    const right = this.rightVec();

    // 3. Longitudinal drive and braking
    let driveForce = 0;
    if (input.throttle) {
      driveForce += ENGINE_POWER * (this.boosting ? BOOST_MULT : 1.0);
    }
    if (input.brake) {
      if (this.speed > 0.5) {
        // Active braking
        driveForce -= BRAKE_POWER;
      } else {
        // Reverse gear
        driveForce -= ENGINE_POWER * 0.85;
      }
    }
    if (input.handbrake) {
      driveForce -= HANDBRAKE_POWER * Math.sign(this.speed || 1);
    }

    // Aerodynamic drag and rolling resistance
    const drag = 0.44 * this.speed * Math.abs(this.speed) * 0.016;
    const rollCoeff = rollingResistance(this.surface);
    const roll = rollCoeff * 650 * Math.sign(this.speed) * (this.onGround ? 1.0 : 0.05);
    const netLongitudinal = driveForce - drag - roll;

    const accel = netLongitudinal / MASS;
    this.speed += accel * dt;
    this.speed = clamp(this.speed, -REVERSE_MAX_SPEED, MAX_SPEED * (this.boosting ? 1.15 : 1.0));

    // 4. Yaw dynamics (bicycle model with lateral tire friction & drift)
    const grip = gripAt(this.surface);
    // Handbrake dramatically breaks rear tire grip to induce intentional drift
    const rearGrip = grip * (input.handbrake ? 0.32 : 1.0);
    const targetYawRate = (this.speed / Math.max(WHEEL_BASE, 0.001)) * Math.tan(this.steer);
    const yawLimit = 2.6 * rearGrip * (0.35 + 0.65 * Math.min(1.0, Math.abs(this.speed) / 9));
    const clampedYawRate = clamp(targetYawRate, -yawLimit, yawLimit);
    this.angularVel = lerp(this.angularVel, clampedYawRate, 1.0 - Math.exp(-9.5 * dt));
    this.yaw += this.angularVel * dt;

    // 5. Integrate positions
    const vyBeforeSpring = this.vel.y;
    this.pos.x += this.vel.x * dt;
    this.pos.z += this.vel.z * dt;
    this.pos.y += this.vel.y * dt;

    // Lateral tire adhesion damping
    const gripDecay = input.handbrake ? 2.4 : 4.8 * grip;
    const carry = Math.exp(-gripDecay * dt) * (this.onGround ? 1.0 : 0.25);
    const vRight = (this.vel.x * right.x + this.vel.z * right.z) * carry;
    this.vel.x = fwd.x * this.speed + right.x * vRight;
    this.vel.z = fwd.z * this.speed + right.z * vRight;

    // Calculate slip for HUD, tire smoke, and skidmarks
    const lateralSpeed = Math.abs(vRight);
    const cornerLoad = Math.min(1.0, (Math.abs(this.angularVel) * Math.abs(this.speed)) / 22);
    const slipTarget = clamp((cornerLoad + lateralSpeed * 0.12) * (1.2 - rearGrip), 0.0, 1.0);
    this.slip = lerp(this.slip, slipTarget, 1.0 - Math.exp(-6.5 * dt));

    // 6. Suspension solve (4 wheels)
    let contacts = 0;
    let avgY = 0;
    let avgNx = 0, avgNz = 0;
    let springForceTotal = 0;
    const cosYaw = Math.cos(this.yaw);
    const sinYaw = Math.sin(this.yaw);

    for (let i = 0; i < 4; i++) {
      const w = WHEELS[i];
      // Rotate wheel offset by vehicle yaw
      const ox = w.x * sinYaw + w.z * cosYaw;
      const oz = w.x * cosYaw - w.z * sinYaw;
      const wx = this.pos.x + ox;
      const wz = this.pos.z + oz;

      const ground = sampleGround(wx, wz);
      // Hub mount point height taking chassis pitch and roll into account
      const mountY = this.pos.y + w.x * Math.sin(this.pitch) + w.z * Math.sin(this.roll);
      const gap = (mountY - WHEEL_RADIUS) - ground.height;
      this.wheelGaps[i] = gap;

      const effectiveGap = clamp(gap, SUSP_BUMP, SUSP_DROOP);
      const comp = SUSP_DROOP - effectiveGap;
      this.suspension[i] = comp;
      this.wheelSlip[i] = this.slip * (w.steered ? 0.6 : 1.0);

      if (gap <= SUSP_DROOP) {
        contacts++;
        // Spring + damper force along +Y
        const fSpring = SUSP_K * comp - SUSP_C * vyBeforeSpring;
        springForceTotal += fSpring;
        avgY += ground.height;
        avgNx += ground.normal.x;
        avgNz += ground.normal.z;
      }
    }

    if (contacts > 0) {
      this.vel.y += (springForceTotal / MASS) * dt;

      // Slope weight transfer: gravity along slope accelerates or retards vehicle
      const normNx = avgNx / contacts;
      const normNz = avgNz / contacts;
      const slopeGrade = clamp(-normNz * fwd.z - normNx * fwd.x, -1.0, 1.0);
      this.speed -= slopeGrade * GRAVITY * 0.35 * dt;

      // Scrub speed on rough slopes
      const rough = Math.abs(normNx) + Math.abs(normNz);
      this.speed -= this.speed * rough * 0.04 * dt;

      // Pitch and roll track ground surface inclination
      const targetPitch = clamp(-normNz * fwd.z - normNx * fwd.x, -0.45, 0.45);
      const targetRoll = clamp(-normNz * right.z - normNx * right.x, -0.45, 0.45);
      this.pitch = lerp(this.pitch, targetPitch, 1.0 - Math.exp(-12.0 * dt));
      this.roll = lerp(this.roll, targetRoll, 1.0 - Math.exp(-12.0 * dt));
    } else {
      // In air: slowly relax pitch and roll towards zero
      this.pitch = lerp(this.pitch, 0, 1.0 - Math.exp(-3.0 * dt));
      this.roll = lerp(this.roll, 0, 1.0 - Math.exp(-3.0 * dt));
    }
    // Gravity applied after spring solve to keep damper stable
    this.vel.y -= GRAVITY * dt;

    this.contactCount = contacts;
    const wasAirborne = this.airborne;
    this.onGround = contacts > 0;
    this.airborne = contacts === 0;

    if (this.airborne) {
      this.airTime += dt;
      // Free-fall safety floor
      const groundFloor = heightAt(this.pos.x, this.pos.z) + WHEEL_RADIUS;
      if (this.pos.y < groundFloor) {
        this.pos.y = groundFloor;
        this.vel.y = Math.abs(this.vel.y) * 0.2;
      }
    } else {
      this.airTime = 0;
      // Hard ground penetration prevention
      const minAllowedY = (avgY / contacts) + WHEEL_RADIUS + SUSP_BUMP;
      if (this.pos.y < minAllowedY) {
        this.pos.y = minAllowedY;
        if (this.vel.y < 0) {
          this.vel.y *= -0.1;
        }
      }
      if (wasAirborne) {
        this.onLanding();
      }
    }

    // Keep jeep within island boundaries
    const islandLimit = TERRAIN.islandStart + 25;
    const r = Math.hypot(this.pos.x, this.pos.z);
    if (r > islandLimit) {
      const scale = islandLimit / r;
      this.pos.x *= scale;
      this.pos.z *= scale;
      this.vel.x *= 0.35;
      this.vel.z *= 0.35;
      this.speed *= 0.5;
    }

    // Surface update and wheel visual spin
    this.surface = surfaceAt(this.pos.x, this.pos.z, this.pos.y);
    this.wheelSpin += (this.speed / WHEEL_RADIUS) * dt;

    // 7. RPM and Gear calculation
    const absSpeed = Math.abs(this.speed);
    if (this.speed < -0.4) {
      this.gear = -1;
      this.rpm = clamp(900 + (absSpeed / REVERSE_MAX_SPEED) * 5200, 900, 6800);
    } else if (absSpeed < 0.2 && !input.throttle) {
      this.gear = 0;
      this.rpm = 900;
    } else {
      let g = 1;
      for (let i = 1; i < GEAR_SPEEDS.length; i++) {
        if (absSpeed >= GEAR_SPEEDS[i] * 0.88) {
          g = i;
        }
      }
      this.gear = clamp(g, 1, 6);
      const gearMin = GEAR_SPEEDS[this.gear - 1] || 0;
      const gearMax = GEAR_SPEEDS[this.gear] || MAX_SPEED;
      const gearRatio = clamp((absSpeed - gearMin) / (gearMax - gearMin || 1), 0, 1);
      const throttleRpmBonus = input.throttle ? 1200 : 0;
      this.rpm = clamp(1200 + gearRatio * 5200 + throttleRpmBonus, 900, 7200);
    }
  }

  onLanding() {
    const impact = clamp(-this.vel.y / 15, 0, 1);
    this.speed *= 1.0 - impact * 0.18;
  }

  get speedKmh() {
    return Math.abs(this.speed) * 3.6;
  }

  // Chassis roll & pitch for 3D visual articulation
  visualPose() {
    const fl = this.suspension[0], fr = this.suspension[1];
    const rl = this.suspension[2], rr = this.suspension[3];
    const suspRoll = ((fl + rl) - (fr + rr)) * 0.35;
    const suspPitch = ((fl + fr) - (rl + rr)) * 0.35;
    // Dynamic lean under lateral drift forces
    const lean = clamp(-this.angularVel * this.speed * 0.015, -0.25, 0.25);
    return {
      roll: clamp(this.roll + suspRoll + lean, -0.55, 0.55),
      pitch: clamp(this.pitch + suspPitch, -0.55, 0.55)
    };
  }
}
